import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Gift, Sparkles, ArrowLeft, Calendar, CreditCard } from 'lucide-react';
import SalePageNavbar from '../../components/SalePageNavbar';
import { useAuthContext } from '../../contexts/AuthContext';

const SalePageBillingSelect: React.FC = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get('plan') as 'basic' | 'premium' || 'basic';
  const [selectedPlan, setSelectedPlan] = useState<'basic' | 'premium'>(planParam);
  const [selectedBilling, setSelectedBilling] = useState<'monthly' | 'halfyearly' | 'yearly'>('');
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);


  const planDetails = {
    basic: {
      name: "Basic",
      monthlyPrice: 149,
      halfYearlyPrice: 745,
      yearlyPrice: 1490,
      features: [
        "วิดีโอ 3 คลิป",
        "รูปภาพไม่จำกัด",
        "ฟีเจอร์พื้นฐานครบ"
      ]
    },
    premium: {
      name: "Premium",
      monthlyPrice: 249,
      halfYearlyPrice: 1245,
      yearlyPrice: 2490,
      features: [
        "วิดีโอ 6 คลิป",
        "รูปภาพไม่จำกัด",
        "ฟีเจอร์ขั้นสูงครบทุกอย่าง"
      ]
    }
  };


  const currentPlan = planDetails[selectedPlan];
  const monthlySavings = (currentPlan.monthlyPrice * 12) - currentPlan.yearlyPrice;
  const halfYearlySavings = (currentPlan.monthlyPrice * 6) - currentPlan.halfYearlyPrice;
  const subdomainValue = 1188; // 99฿ x 12 เดือน


  const handleProceed = async () => {
    // ตรวจสอบ Step 1
    if (!selectedPlan) {
      alert('⚠️ กรุณาเลือกแพ็กเกจก่อน (Step 1)');
      setCurrentStep(1);
      return;
    }
    
    // ตรวจสอบ Step 2
    if (!selectedBilling) {
      alert('⚠️ กรุณาเลือกรอบชำระเงินก่อน (Step 2)');
      setCurrentStep(2);
      return;
    }

    let finalPrice = currentPlan.monthlyPrice;
    if (selectedBilling === 'halfyearly') {
      finalPrice = currentPlan.halfYearlyPrice;
    } else if (selectedBilling === 'yearly') {
      finalPrice = currentPlan.yearlyPrice;
    }

    // Save checkout data with isNewPurchase flag
    const checkoutData = {
      isNewPurchase: true,
      source: 'salepage',
      plan: selectedPlan,
      billing: selectedBilling,
      amount: finalPrice
    };
    localStorage.setItem('salePageCheckout', JSON.stringify(checkoutData));

    const params = new URLSearchParams({
      source: 'salepage',
      plan: selectedPlan,
      billing: selectedBilling,
      amount: finalPrice.toString()
    });

    navigate(`/subscription/checkout-new?${params.toString()}`);
  };

  return (
    <>
      <SalePageNavbar />
      <div className="min-h-screen bg-[#F3F8F8] py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Back Button */}
          <button
            onClick={() => navigate('/salepage/pricing')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">กลับ</span>
          </button>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              เลือกแพ็กเกจของคุณ
            </h1>
            <p className="text-xl text-gray-600">
              เลือกแพ็กเกจ รอบการชำระเงิน และ Add-ons ที่คุณต้องการ
            </p>
          </div>

          {/* 2 Column Layout */}
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Steps */}
            <div className="lg:col-span-2 space-y-6">

          {/* Step 1: Choose Plan */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300">
            <button
              onClick={() => setCurrentStep(1)}
              className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold bg-gradient-to-r from-purple-600 to-pink-600 text-white">1</div>
                <h2 className="text-2xl font-bold text-gray-900">Step 1: เลือกแพ็กเกจ</h2>
              </div>
              {selectedPlan && currentStep !== 1 && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-semibold">{currentPlan.name}</span>
                  <Check className="w-5 h-5 text-green-600" />
                </div>
              )}
            </button>
            
            {currentStep === 1 && (
            <div className="p-6 pt-0">
            <div className="grid md:grid-cols-2 gap-4">
              {/* Basic Plan */}
              <button
                onClick={() => setSelectedPlan('basic')}
                className={`relative p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                  selectedPlan === 'basic'
                    ? "border-teal-500 bg-teal-50 shadow-lg transform scale-105"
                    : "border-gray-200 hover:border-teal-300 hover:shadow-md"
                }`}
              >
                {selectedPlan === 'basic' && (
                  <div className="absolute -top-3 -right-3 bg-teal-500 text-white p-2 rounded-full shadow-lg">
                    <Check className="w-5 h-5" />
                  </div>
                )}
                
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-7 h-7 text-teal-600" />
                  <h3 className="text-2xl font-bold text-gray-900">Basic</h3>
                </div>
                
                <div className="text-3xl font-black text-teal-600 mb-4">
                  ฿149<span className="text-sm text-gray-600 font-normal">/เดือน</span>
                </div>

                <ul className="space-y-2 text-sm">
                  {planDetails.basic.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </button>

              {/* Premium Plan */}
              <button
                onClick={() => setSelectedPlan('premium')}
                className={`relative p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                  selectedPlan === 'premium'
                    ? "border-orange-500 bg-orange-50 shadow-lg transform scale-105"
                    : "border-gray-200 hover:border-orange-300 hover:shadow-md"
                }`}
              >
                {selectedPlan === 'premium' && (
                  <div className="absolute -top-3 -right-3 bg-orange-500 text-white p-2 rounded-full shadow-lg">
                    <Check className="w-5 h-5" />
                  </div>
                )}
                
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-7 h-7 text-orange-600" />
                  <h3 className="text-2xl font-bold text-gray-900">Premium</h3>
                  <span className="text-xs bg-gradient-to-r from-orange-500 to-amber-500 text-white px-2 py-1 rounded-full font-bold">
                    แนะนำ
                  </span>
                </div>
                
                <div className="text-3xl font-black text-orange-600 mb-4">
                  ฿249<span className="text-sm text-gray-600 font-normal">/เดือน</span>
                </div>

                <ul className="space-y-2 text-sm">
                  {planDetails.premium.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </button>
            </div>
            </div>
            )}
          </div>

          {/* Step 2: Choose Billing Cycle */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300">
            <button
              onClick={() => setCurrentStep(2)}
              className="w-full flex items-center justify-between p-6 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold bg-gradient-to-r from-purple-600 to-pink-600 text-white">2</div>
                <h2 className="text-2xl font-bold text-gray-900">Step 2: เลือกรอบชำระเงิน</h2>
              </div>
              {selectedBilling && currentStep !== 2 && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="font-semibold">
                    {selectedBilling === 'monthly' ? 'รายเดือน' : selectedBilling === 'halfyearly' ? 'ราย 6 เดือน' : 'รายปี'}
                  </span>
                  <Check className="w-5 h-5 text-green-600" />
                </div>
              )}
            </button>
            
            {currentStep === 2 && (
            <div className="p-6 pt-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Monthly */}
            <button
              onClick={() => setSelectedBilling('monthly')}
              className={`relative rounded-2xl p-6 transition-all ${
                selectedBilling === 'monthly'
                  ? 'bg-white shadow-xl ring-4 ring-blue-500 transform scale-105'
                  : 'bg-white/80 shadow-lg hover:shadow-xl'
              }`}
            >
              {selectedBilling === 'monthly' && (
                <div className="absolute top-4 right-4 bg-blue-500 text-white rounded-full p-1">
                  <Check className="w-4 h-4" />
                </div>
              )}

              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-5 h-5 text-blue-500" />
                <h3 className="text-xl font-bold text-gray-900">รายเดือน</h3>
              </div>

              <div className="mb-3">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold text-gray-900">{currentPlan.monthlyPrice}</span>
                  <span className="text-lg text-gray-600">฿ / เดือน</span>
                </div>
              </div>

              <div className="space-y-1 text-sm text-gray-600">
                <p className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  ยืดหยุ่น ไม่ผูกมัด
                </p>
              </div>
            </button>

            {/* Half Yearly */}
            <button
              onClick={() => setSelectedBilling('halfyearly')}
              className={`relative rounded-2xl p-6 transition-all ${
                selectedBilling === 'halfyearly'
                  ? 'bg-gradient-to-br from-blue-500 to-cyan-500 text-white shadow-2xl ring-4 ring-blue-400 transform scale-105'
                  : 'bg-white/80 shadow-lg hover:shadow-xl'
              }`}
            >
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                ฟรี 1 เดือน
              </div>

              {selectedBilling === 'halfyearly' && (
                <div className="absolute top-4 right-4 bg-white/30 backdrop-blur-md text-white rounded-full p-1">
                  <Check className="w-4 h-4" />
                </div>
              )}

              <div className="flex items-center gap-2 mb-3">
                <Calendar className={`w-5 h-5 ${selectedBilling === 'halfyearly' ? 'text-white' : 'text-blue-500'}`} />
                <h3 className={`text-xl font-bold ${selectedBilling === 'halfyearly' ? 'text-white' : 'text-gray-900'}`}>
                  ราย 6 เดือน
                </h3>
              </div>

              <div className="mb-3">
                <div className="flex items-baseline gap-1 mb-1">
                  <span className={`text-lg line-through ${selectedBilling === 'halfyearly' ? 'text-white/60' : 'text-gray-400'}`}>
                    {currentPlan.monthlyPrice * 6}฿
                  </span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className={`text-3xl font-bold ${selectedBilling === 'halfyearly' ? 'text-white' : 'text-gray-900'}`}>
                    {currentPlan.halfYearlyPrice}
                  </span>
                  <span className={`text-lg ${selectedBilling === 'halfyearly' ? 'text-white/90' : 'text-gray-600'}`}>฿</span>
                </div>
                <p className={`text-xs mt-1 ${selectedBilling === 'halfyearly' ? 'text-white/80' : 'text-gray-500'}`}>
                  เฉลี่ย {Math.round(currentPlan.halfYearlyPrice / 6)}฿/เดือน
                </p>
              </div>

              <div className="text-xs">
                <p className={`flex items-center gap-1.5 font-semibold ${selectedBilling === 'halfyearly' ? 'text-white' : 'text-blue-600'}`}>
                  <Gift className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>ประหยัด {halfYearlySavings}฿</span>
                </p>
              </div>
            </button>

            {/* Yearly */}
            <button
              onClick={() => setSelectedBilling('yearly')}
              className={`relative rounded-2xl p-6 transition-all ${
                selectedBilling === 'yearly'
                  ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-2xl ring-4 ring-purple-400 transform scale-105'
                  : 'bg-white/80 shadow-lg hover:shadow-xl'
              }`}
            >
              {/* Recommended Badge */}
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                <span>คุ้มสุด</span>
              </div>

              {selectedBilling === 'yearly' && (
                <div className="absolute top-4 right-4 bg-white/30 backdrop-blur-md text-white rounded-full p-1">
                  <Check className="w-4 h-4" />
                </div>
              )}

              <div className="flex items-center gap-2 mb-3">
                <Calendar className={`w-5 h-5 ${selectedBilling === 'yearly' ? 'text-white' : 'text-purple-500'}`} />
                <h3 className={`text-xl font-bold ${selectedBilling === 'yearly' ? 'text-white' : 'text-gray-900'}`}>
                  รายปี
                </h3>
              </div>

              <div className="mb-3">
                <div className="flex items-baseline gap-1 mb-1">
                  <span className={`text-lg line-through ${selectedBilling === 'yearly' ? 'text-white/60' : 'text-gray-400'}`}>
                    {currentPlan.monthlyPrice * 12}฿
                  </span>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className={`text-3xl font-bold ${selectedBilling === 'yearly' ? 'text-white' : 'text-gray-900'}`}>
                    {currentPlan.yearlyPrice}
                  </span>
                  <span className={`text-lg ${selectedBilling === 'yearly' ? 'text-white/90' : 'text-gray-600'}`}>฿</span>
                </div>
                <p className={`text-xs mt-1 ${selectedBilling === 'yearly' ? 'text-white/80' : 'text-gray-500'}`}>
                  เฉลี่ย {Math.round(currentPlan.yearlyPrice / 12)}฿/เดือน
                </p>
              </div>

              <div className="text-xs">
                <p className={`flex items-center gap-1.5 font-semibold ${selectedBilling === 'yearly' ? 'text-white' : 'text-purple-600'}`}>
                  <Gift className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>ฟรี 2 เดือน - ประหยัด {monthlySavings}฿</span>
                </p>
                {selectedPlan === 'premium' && (
                  <p className={`flex items-center gap-1.5 font-semibold mt-1 ${selectedBilling === 'yearly' ? 'text-white' : 'text-purple-600'}`}>
                    <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>Subdomain ฟรี!</span>
                  </p>
                )}
              </div>
            </button>
            </div>
            </div>
            )}
          </div>

          {/* Step 3: Removed - URL setup moved to settings page */}

            </div>
            {/* End Left Column */}

            {/* Right Column - Summary (Sticky) */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
          {/* Summary */}
          <div className="bg-white rounded-2xl p-6 shadow-xl border-2 border-purple-100">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2 text-base">
              <CreditCard className="w-5 h-5 text-purple-500" />
              สรุปการสั่งซื้อ
            </h3>
            <div className="space-y-2.5">
              <div className="flex justify-between items-center">
                <span className="text-gray-700 text-sm">แพ็คเก็จ:</span>
                <span className="font-bold text-gray-900 text-sm">{currentPlan.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700 text-sm">รอบชำระเงิน:</span>
                <span className="font-bold text-gray-900 text-sm">
                  {selectedBilling === 'monthly' ? 'รายเดือน' : selectedBilling === 'halfyearly' ? 'ราย 6 เดือน' : selectedBilling === 'yearly' ? 'รายปี' : '-'}
                </span>
              </div>
              <div className="flex justify-between items-center pt-2.5 border-t border-gray-200">
                <span className="text-base font-bold text-gray-900">ราคารวม:</span>
                <span className="text-xl font-bold text-purple-600">
                  {selectedBilling === 'monthly' 
                    ? currentPlan.monthlyPrice 
                    : selectedBilling === 'halfyearly' 
                    ? currentPlan.halfYearlyPrice 
                    : currentPlan.yearlyPrice}฿
                </span>
              </div>
              {selectedBilling === 'halfyearly' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                  <div className="flex justify-between items-center">
                    <span className="text-blue-700 font-semibold text-xs">ส่วนลดแพ็คเก็จ (ฟรี 1 เดือน):</span>
                    <span className="text-blue-700 font-bold text-xs">-{halfYearlySavings}฿</span>
                  </div>
                </div>
              )}
              {selectedBilling === 'yearly' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-2.5">
                  <div className="flex justify-between items-center">
                    <span className="text-green-700 font-semibold text-xs">ส่วนลดแพ็คเก็จ (ฟรี 2 เดือน):</span>
                    <span className="text-green-700 font-bold text-xs">-{monthlySavings}฿</span>
                  </div>
                  {selectedPlan === 'premium' && (
                    <div className="flex justify-between items-center mt-1.5 pt-1.5 border-t border-green-200">
                      <span className="text-green-700 text-xs">Subdomain ฟรี (มูลค่า):</span>
                      <span className="text-green-700 font-bold text-xs">{subdomainValue}฿</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* CTA Button */}
          <button
            onClick={handleProceed}
            className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold text-lg hover:shadow-2xl transition-all transform hover:scale-105"
          >
            ดำเนินการต่อ
          </button>

          {/* Features Reminder */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h4 className="font-semibold text-blue-900 mb-2">ฟีเจอร์ที่คุณจะได้รับ:</h4>
            <ul className="space-y-1 text-sm text-blue-800">
              {currentPlan.features.map((feature, index) => (
                <li key={index} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-blue-600" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
              </div>
            </div>
            {/* End Right Column */}
          </div>
          {/* End Grid */}
        </div>
      </div>
    </>
  );
};

export default SalePageBillingSelect;
